import { Component, OnInit, inject } from '@angular/core';
import {
  FormGroup,
  FormBuilder,
  Validators,
  ReactiveFormsModule,
  FormControl,
  ValidatorFn,
  ValidationErrors,
  AbstractControl,
} from '@angular/forms';
import { DatePipe } from '@angular/common';
import { TextInputComponent } from '../../../../../shared/components/text-input/text-input.component';
import { CheckboxComponent } from '../../../../../shared/components/checkbox/checkbox.component';
import { PrimaryButtonComponent } from '../../../../../shared/components/primary-button/primary-button.component';
import { OrganizationService } from '../../services/organization.service';
import { MatIconModule } from '@angular/material/icon';
import { TranslocoModule } from '@jsverse/transloco';
import { DataProcessingAgreementResponseDTO } from '../../models/DataProcessingAgreementDTO';
import { SnackbarService } from '../../../../../core/services/snackbar.service';
import { CustomPortalError } from '../../../../../core/errors/models/CustomPortalError';
import { AnalyticsService } from '../../../../../core/services/analytics/analytics.service';

export interface ContractFormControls {
  agreement: FormControl<boolean>;
  date: FormControl<string>;
  fullName: FormControl<string>;
  responsibleBody: FormControl<string>;
}

@Component({
  selector: 'app-data-processing-agreement',
  imports: [
    ReactiveFormsModule,
    TextInputComponent,
    CheckboxComponent,
    PrimaryButtonComponent,
    MatIconModule,
    TranslocoModule,
  ],
  providers: [DatePipe],
  templateUrl: './data-processing-agreement.component.html',
  styleUrl: './data-processing-agreement.component.css',
})
export class DataProcessingAgreementComponent implements OnInit {
  private readonly analyticsService = inject(AnalyticsService);
  private readonly fb = inject(FormBuilder);
  private readonly datePipe = inject(DatePipe);
  private readonly organizationService = inject(OrganizationService);
  private readonly snackbarService = inject(SnackbarService);

  contractForm: FormGroup<ContractFormControls>;
  isLoading: boolean;

  ngOnInit(): void {
    const currentDate = this.formatDate(new Date());
    this.contractForm = this.fb.group<ContractFormControls>({
      agreement: this.fb.nonNullable.control(false, Validators.requiredTrue),
      date: this.fb.nonNullable.control(
        { value: currentDate, disabled: true },
        Validators.required,
      ),
      fullName: this.fb.nonNullable.control('', [
        Validators.required,
        Validators.maxLength(50),
        this.forbiddenCharactersValidator(),
      ]),
      responsibleBody: this.fb.nonNullable.control('', [
        Validators.maxLength(50),
        this.forbiddenCharactersValidator(),
      ]),
    });

    this.getDataProcessingAgreement();
  }

  onSubmit(): void {
    const timestamp =
      this.formatDateString(new Date()) +
      ' ' +
      this.formatTimeString(new Date());
    this.contractForm.disable();
    this.isLoading = true;
    this.organizationService
      .saveDataProcessingAgreement(this.contractForm.value)
      .subscribe({
        next: () => {
          this.isLoading = false;
          this.getDataProcessingAgreement();
        },
        complete: () => {
          this.snackbarService.success(
            'portal.settings.dataProcessingContract.successfulDPASubmissionSnackbar',
          );
          this.analyticsService.track('dpa_signed', { timestamp: timestamp });
        },
        error: (error: CustomPortalError) => {
          this.isLoading = false;
          this.snackbarService.error(error.errorType);
        },
      });
  }

  download(fileName: string) {
    this.organizationService.downloadFile(fileName).subscribe((data) => {
      this.openFile(data);
    });
  }

  private openFile(data: Blob) {
    const blob = new Blob([data], { type: 'application/pdf' });
    const url = window.URL.createObjectURL(blob);
    window.open(url);
  }

  private getDataProcessingAgreement() {
    this.organizationService.getDataProcessingAgreement().subscribe({
      next: (res: DataProcessingAgreementResponseDTO | null) => {
        const dpa = res?.dataProcessingAgreement;
        if (!dpa) {
          this.contractForm.enable();
          this.contractForm.controls.date.disable();
          this.contractForm.patchValue({ agreement: false });
          return;
        }

        this.contractForm.patchValue({
          agreement: true,
          date: this.formatDate(dpa.date),
          fullName: dpa.signatory,
          responsibleBody: dpa.responsibleBody ?? '-',
        });
        this.contractForm.disable();
      },
      error: () => {
        console.info('No data processing contract signed');
      },
    });
  }

  private formatDate(date: Date | string): string {
    return this.datePipe.transform(date, 'dd.MM.yyyy') ?? '';
  }

  private formatDateString(date: Date | string): string {
    return this.datePipe.transform(date, 'yyyy-MM-dd', 'Europe/Berlin') ?? '';
  }

  private formatTimeString(date: Date): string {
    return date.toLocaleTimeString('en-GB', {
      timeZone: 'Europe/Berlin',
      hour12: false,
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      timeZoneName: 'longOffset',
    });
  }

  private forbiddenCharactersValidator(): ValidatorFn {
    const forbiddenChars = /[@#$%^*_!+=[\]{}|:?~";<>]/g;
    return (control: AbstractControl<string>): ValidationErrors | null => {
      const matches = control.value.match(forbiddenChars);
      return matches
        ? {
            forbiddenCharacters: {
              chars: [...new Set(matches)].join(', '),
            },
          }
        : null;
    };
  }
}
